import type { ListFieldFormatting } from '@ardoq/api-types';
import { buildColorWarningKey } from '../colorWarnings';
import type { FormattingContextState } from '../types';
import type { FormattingRuleBodyProperties } from './types';
import { Stack } from '@ardoq/layout';
import { ListFieldFormattingRuleColorRow } from './ListFieldFormattingRuleColorRow';
import styled from 'styled-components';

const ListFieldRuleBodyStack = styled(Stack)`
  width: 100%;
`;

type ColorRowContext = {
  ruleId: string;
  fieldValue: string;
  listFieldFormatting: ListFieldFormatting;
  formattingContext: FormattingContextState;
};

const rowColorValueChange = (args: ColorRowContext) => {
  const {
    ruleId,
    fieldValue,
    listFieldFormatting,
    formattingContext: { updateRule },
  } = args;
  return (newColor: string) =>
    updateRule(ruleId, {
      value: {
        ...listFieldFormatting,
        [fieldValue]: {
          color: newColor,
          isDisabled: Boolean(listFieldFormatting[fieldValue]?.isDisabled),
        },
      },
    });
};

const rowIsDisabledToggle = (args: ColorRowContext) => {
  const {
    ruleId,
    fieldValue,
    listFieldFormatting,
    formattingContext: { updateRule },
  } = args;
  return () =>
    updateRule(ruleId, {
      value: {
        ...listFieldFormatting,
        [fieldValue]: {
          color: listFieldFormatting[fieldValue]?.color || '',
          isDisabled: !listFieldFormatting[fieldValue]?.isDisabled,
        },
      } satisfies ListFieldFormatting,
    });
};

/** The body of a List field formatting rule in the Perspectives Sidebar. */
const ListFieldFormattingRuleBody = ({
  rule,
  formattingContext,
}: FormattingRuleBodyProperties) => {
  const { color, __id: ruleId } = rule;
  const { ruleInfo } = formattingContext;
  const info = ruleInfo[ruleId];
  if (!info?.listFieldFormatting) {
    return null;
  }
  const { listFieldFormatting, colorWarnings } = info;

  return (
    <>
      <ListFieldRuleBodyStack gap="small">
        {Object.keys(listFieldFormatting).map(fieldValue => {
          const context: ColorRowContext = {
            ruleId,
            fieldValue,
            listFieldFormatting,
            formattingContext,
          };
          const itemColor = listFieldFormatting[fieldValue]?.color;
          const colorWarning = itemColor
            ? colorWarnings[buildColorWarningKey(itemColor, fieldValue)]
            : undefined;
          return (
            <ListFieldFormattingRuleColorRow
              key={`${fieldValue}:${color}`}
              fieldValue={fieldValue}
              listFieldFormatting={listFieldFormatting}
              onColorValueChange={rowColorValueChange(context)}
              onIsDisabledToggle={rowIsDisabledToggle(context)}
              colorWarning={colorWarning}
            />
          );
        })}
      </ListFieldRuleBodyStack>
    </>
  );
};
export { ListFieldFormattingRuleBody };
